import { FetchService } from "./services/fetchService";

const DEFAULT_ROUTES = Object.freeze({
    assets: Object.freeze({
        logoUri: '/ptza_storage/assets/image/logo.webp'
    }),
    tienda: Object.freeze({
        home: '/tienda/index.html',
        carrito: '/tienda/carrito.html',
        checkout: '/tienda/checkout.html',
        resumen: '/tienda/resumen-pedido.html',
        item: '/tienda/item/ver.html',
        buscadorItem: '/tienda/buscar-item.html',
        pagar: '/tienda/pagar.html'
    }),
    web: Object.freeze({
        home: '/index.html',
        tyc: '/terminos-y-condiciones.html',
        ayuda: '/ayuda/ver.html',
        noticia: '/noticia/ver.html',
        contacto: '/contacto.html'
    })
});

class ConfigPtza {

    #existConfigFile = false;
    #configObj = null;
    #routes = null;
    #routesSource = 'default';
    #modo = 'local';
    #fetchService = new FetchService();

    // LOAD CONFIG
    async loadConfig() {
        /*
         * ETAPA 2 / ITERACIÓN 11 - CONFIG ROOT + BINDINGS
         *
         * Autoridades runtime:
         *
         * /ptza_core/client/config.json
         *   -> environment
         *   -> ptza
         *   -> clientInfo
         *   -> addons   (override sobre PTZA_PAGE_CONFIG.addons)
         *   -> meta     (override sobre PTZA_PAGE_CONFIG.meta)
         *
         * /ptza_core/client/routes.json
         *   -> rutas opcionales del tenant
         *
         * window.PTZA_PAGE_CONFIG
         *   -> page
         *   -> addons   (fallback de página)
         *   -> meta     (fallback de página)
         *
         * `page`, `addons` y `meta` quedan como ramas hermanas del objeto
         * runtime. addons/meta ya no viven dentro de page.
         */
        const rawPageConfig = this.parseObject(
            window.PTZA_PAGE_CONFIG
        ) || {};

        const rawPageSection = this.parseObject(
            rawPageConfig.page
        ) || {};

        const pageConfig = this.normalizePageConfig(rawPageSection);

        // Compatibilidad transitoria: si una página vieja todavía define
        // page.addons/page.meta, se usan sólo como fallback de entrada.
        const pageAddons = this.normalizeConfigSection(
            rawPageConfig.addons ?? rawPageSection.addons ?? {}
        );

        const pageMeta = this.normalizeConfigSection(
            rawPageConfig.meta ?? rawPageSection.meta ?? {}
        );

        const [rawConfigFile, rawRoutesFile] = await Promise.all([
            this.#fetchService.loadJSONFile(
                '/ptza_core/client/config.json',
                { fallback: null }
            ),
            this.#fetchService.loadJSONFile(
                '/ptza_core/client/routes.json',
                { fallback: {} }
            )
        ]);

        const configFile = this.parseObject(rawConfigFile);
        const tenantConfig = this.normalizeTenantConfig(configFile);
        const routesConfig = this.normalizeRoutes(rawRoutesFile);

        // Merge superficial intencional: config.json siempre gana por clave,
        // incluso cuando el valor del servidor sea '', false o 0.
        const configAddons = this.normalizeConfigSection(
            configFile?.addons ?? {}
        );
        const configMeta = this.normalizeConfigSection(
            configFile?.meta ?? {}
        );

        const addonsConfig = {
            ...pageAddons,
            ...configAddons
        };

        const metaConfig = {
            ...pageMeta,
            ...configMeta
        };

        if (!tenantConfig) {
            console.error(
                'Propuestaza: config.json no contiene una configuración válida del tenant.'
            );
            this.#existConfigFile = false;
            this.#configObj = null;
            this.#routes = routesConfig.routes;
            this.#routesSource = routesConfig.source;
            this.#modo = 'local';
            return false;
        }

        this.#existConfigFile = true;
        this.#routes = routesConfig.routes;
        this.#routesSource = routesConfig.source;
        this.#configObj = {
            ...tenantConfig,
            addons: addonsConfig,
            meta: metaConfig,
            page: pageConfig
        };

        await this.verifyApiConnection();

        return true;
    }

    async verifyApiConnection() {
        this.#modo = 'local';

        if (!this.#existConfigFile || !this.hasApiConnectionData(this.#configObj)) {
            return false;
        }

        try {
            // Import dinámico para evitar construir ApiService antes de que
            // ConfigPtza termine de establecer la configuración activa.
            const { apiService } = await import('./services/apiService.js');
            const connected = await apiService.loginSiteApi();

            if (!connected) {
                this.#modo = 'local';
                console.warn('Propuestaza: API no disponible. Modo local.');
                return false;
            }

            this.#modo = 'api';
            console.log('Propuestaza: Modo API.');
            return true;
        } catch (error) {
            this.#modo = 'local';
            console.warn('Propuestaza: API no disponible. Modo local.');
            return false;
        }
    }

    hasApiConnectionData(config) {
        return Boolean(
            config?.ptza?.apiPTZAUrl &&
            config?.ptza?.publicKeyClient &&
            config?.ptza?.clientID
        );
    }

    parseObject(value) {
        if (!value) {
            return null;
        }

        if (typeof value === 'string') {
            try {
                value = JSON.parse(value);
            } catch (e) {
                return null;
            }
        }

        if (typeof value !== 'object' || Array.isArray(value)) {
            return null;
        }

        return value;
    }

    normalizeTenantConfig(config) {
        config = this.parseObject(config);

        if (!config || !config.clientInfo || typeof config.clientInfo !== 'object') {
            return null;
        }

        const normalized = {
            environment: config.environment || 'prod',

            ptza: {
                apiPTZAUrl: config?.ptza?.apiPTZAUrl || '',
                publicKeyClient: config?.ptza?.publicKeyClient || '',
                clientID: config?.ptza?.clientID || ''
            },

            clientInfo: {
                nombreCliente: config?.clientInfo?.nombreCliente || '',
                clientDomain: config?.clientInfo?.clientDomain || '',
                clientProtocol: config?.clientInfo?.clientProtocol || '',
                urlCliente: config?.clientInfo?.urlCliente || '',

                numeroCelular: config?.clientInfo?.numeroCelular || '',
                numeroTelefono: config?.clientInfo?.numeroTelefono || '',
                direccion: config?.clientInfo?.direccion || '',
                email: config?.clientInfo?.email || '',

                facebook: config?.clientInfo?.facebook || '',
                tiktok: config?.clientInfo?.tiktok || '',
                instagram: config?.clientInfo?.instagram || '',
                youtube: config?.clientInfo?.youtube || '',
                horarioComercial: config?.clientInfo?.horarioComercial || ''
            }
        };

        return this.validateTenantConfig(normalized)
            ? normalized
            : null;
    }

    cloneDefaultRoutes() {
        return {
            assets: { ...DEFAULT_ROUTES.assets },
            tienda: { ...DEFAULT_ROUTES.tienda },
            web: { ...DEFAULT_ROUTES.web }
        };
    }

    isEmptyObject(value) {
        return Boolean(
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.keys(value).length === 0
        );
    }

    normalizeRoutes(routes) {
        routes = this.parseObject(routes);

        if (!routes || this.isEmptyObject(routes)) {
            return {
                source: 'default',
                routes: this.cloneDefaultRoutes()
            };
        }

        // Formato canónico: { assets, tienda, web }.
        // Se acepta { uriTree: {...} } sólo como compatibilidad transitoria.
        const source = this.parseObject(routes.uriTree) || routes;
        const normalized = this.cloneDefaultRoutes();

        const assets = this.parseObject(source.assets);
        const tienda = this.parseObject(source.tienda);
        const web = this.parseObject(source.web);

        if (assets) {
            normalized.assets = {
                ...normalized.assets,
                ...assets,
                logoUri:
                    assets.logoUri
                    || assets.logo
                    || normalized.assets.logoUri
            };

            // `logo` se admite como entrada legacy, pero no se conserva en el
            // objeto runtime canónico.
            delete normalized.assets.logo;
        }

        if (tienda) {
            normalized.tienda = {
                ...normalized.tienda,
                ...tienda
            };
        }

        if (web) {
            normalized.web = {
                ...normalized.web,
                ...web
            };
        }

        return {
            source: 'file',
            routes: normalized
        };
    }

    normalizeConfigSection(section) {
        section = this.parseObject(section);

        if (!section) {
            return {};
        }

        return { ...section };
    }

    normalizePageConfig(page) {
        page = this.parseObject(page);

        if (!page) {
            return {};
        }

        // `addons` y `meta` ya son ramas hermanas de `page`. Si una página
        // legacy todavía los trae anidados, se toman como fallback en
        // loadConfig(), pero no se conservan dentro de page runtime.
        const {
            addons: _legacyAddons,
            meta: _legacyMeta,
            ...pageOnly
        } = page;

        return pageOnly;
    }

    validateTenantConfig(config) {
        if (!config || !config.clientInfo || !config.ptza) {
            return false;
        }

        /*
         * clientInfo es obligatorio como bloque porque config.json es ahora la
         * única autoridad del tenant. Los campos concretos conservan defaults y
         * pueden estar vacíos si esa funcionalidad no se utiliza.
         *
         * ptza también existe siempre como bloque normalizado, pero puede quedar
         * vacío: eso representa un tenant operando en modo local.
         */
        return true;
    }

    getConfig() {
        return this.#configObj;
    }

    getPageConfig() {
        return this.#configObj?.page || null;
    }

    getAddonsConfig() {
        return this.#configObj?.addons || {};
    }

    getMetaConfig() {
        return this.#configObj?.meta || {};
    }

    getAddonsValue(key, fallback = null) {
        const config = this.getAddonsConfig();
        return config?.[key] ?? fallback;
    }

    getMetaValue(key, fallback = null) {
        const config = this.getMetaConfig();
        return config?.[key] ?? fallback;
    }

    getValueByPath(path, fallback = undefined) {
        const normalizedPath = String(path ?? '').trim();

        if (!normalizedPath || !this.#configObj) {
            return fallback;
        }

        const forbidden = new Set([
            '__proto__',
            'prototype',
            'constructor'
        ]);

        const parts = normalizedPath
            .split('.')
            .map((part) => part.trim())
            .filter(Boolean);

        if (!parts.length || parts.some((part) => forbidden.has(part))) {
            return fallback;
        }

        let current = this.#configObj;

        for (const part of parts) {
            if (
                current === null ||
                current === undefined ||
                typeof current !== 'object' ||
                !Object.prototype.hasOwnProperty.call(current, part)
            ) {
                return fallback;
            }

            current = current[part];
        }

        return current;
    }

    getClientInfoValue(key, fallback = null) {
        const config = this.#configObj?.clientInfo;

        if (!config || typeof config !== 'object') {
            return fallback;
        }

        return config[key] ?? fallback;
    }

    getPtzaValue(key, fallback = null) {
        const config = this.#configObj?.ptza;

        if (!config || typeof config !== 'object') {
            return fallback;
        }

        return config[key] ?? fallback;
    }

    getPageValue(key, fallback = null) {
        const config = this.#configObj?.page;

        if (!config || typeof config !== 'object') {
            return fallback;
        }

        return config[key] ?? fallback;
    }

    getRoutes() {
        return this.#routes || this.cloneDefaultRoutes();
    }

    getRoutesSource() {
        return this.#routesSource;
    }

    getUri(type, key, fallback = null){
        const routes = this.getRoutes();

        if (!routes || typeof routes !== 'object') {
            return fallback;
        }

        return routes?.[type]?.[key] ?? fallback;
    }

    getModo(){
        return this.#modo;
    }

    existConfigFile(){
        return this.#existConfigFile;
    }

    isLoadConfig(){
        return (this.#configObj)?true:false;
    }
}

export const ptzaConfig = new ConfigPtza();
